import React from "react";
import { PROGRAM } from "@/lib/eventData";
import SectionHeader from "@/components/gala/SectionHeader";
import { Hibiscus, Frangipani } from "@/components/gala/TropicalDecor";

export default function ProgramTimeline() {
  return (
    <section
      id="program"
      data-testid="program-section"
      className="section relative overflow-hidden"
    >
      {/* Tropical decor */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <Hibiscus className="absolute top-20 -right-16 w-72 text-[#D9C7A0]/10 rotate-6" />
        <Frangipani className="absolute bottom-32 -left-16 w-64 text-[#F3E08A]/10 -rotate-12" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeader
          eyebrow="Havana Night Show"
          title="An Evening of Celebration, Entertainment & Connection"
          description="The Havana Night Show sets the tempo — a broadcast-styled run of show from red carpet arrivals through the finale, with plenty of rhythm, awards and unscripted chaos along the way."
        />

        <div className="relative mt-10">
          {/* Center line */}
          <div
            aria-hidden
            className="hidden md:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 timeline-line"
          />

          <ul className="space-y-10 md:space-y-14">
            {(() => {
              let prevChapter = null;
              let chapterCursor = 0;
              return PROGRAM.map((item, idx) => {
                const chapterChanged = item.chapter !== prevChapter;
                if (chapterChanged) {
                  chapterCursor = 0;
                  prevChapter = item.chapter;
                }
                const isLeft = chapterCursor % 2 === 0;
                chapterCursor += 1;

                return (
                  <React.Fragment key={idx}>
                    {chapterChanged && (
                      <li
                        data-testid={`program-chapter-${item.chapter.replace(/\s+/g, "-").toLowerCase()}`}
                        className="relative z-10 py-2"
                      >
                        <ChapterBanner title={item.chapter} />
                      </li>
                    )}
                    <li
                      data-testid={`program-item-${idx}`}
                      className="relative md:grid md:grid-cols-2 md:gap-24 rise"
                      style={{ animationDelay: `${idx * 90}ms` }}
                    >
                      {/* Marker */}
                      <div
                        aria-hidden
                        className="hidden md:block absolute top-3 left-1/2 -translate-x-1/2 z-10"
                      >
                        <div className="w-4 h-4 rotate-45 bg-[#0B2820] border border-[#D4AF37]" />
                        <div className="absolute inset-0 m-auto w-1.5 h-1.5 rotate-45 bg-[#D4AF37]" />
                      </div>

                      {/* Connector line from center spine to content */}
                      <div
                        aria-hidden
                        className={`hidden md:block absolute top-[22px] h-px bg-gradient-to-r ${
                          isLeft
                            ? "right-1/2 mr-2 from-transparent via-[#D4AF37]/60 to-[#D4AF37]"
                            : "left-1/2 ml-2 from-[#D4AF37] via-[#D4AF37]/60 to-transparent"
                        }`}
                        style={{ width: "calc(50% - 5rem)" }}
                      />

                      {isLeft ? (
                        <>
                          <div className="md:pr-16 md:text-right">
                            <TimelineCard item={item} align="right" />
                          </div>
                          <div className="hidden md:block" />
                        </>
                      ) : (
                        <>
                          <div className="hidden md:block" />
                          <div className="md:pl-16">
                            <TimelineCard item={item} align="left" />
                          </div>
                        </>
                      )}
                    </li>
                  </React.Fragment>
                );
              });
            })()}
          </ul>
        </div>
      </div>
    </section>
  );
}

function TimelineCard({ item, align }) {
  return (
    <div className="py-2">
      <div
        className={`font-display text-[10px] tracking-[0.35em] text-[#F3E08A] mb-2 ${
          align === "right" ? "md:text-right" : "md:text-left"
        }`}
      >
        {item.time}
      </div>
      <h3 className="font-serif-luxe text-xl sm:text-2xl text-[#F8F1DC] leading-snug mb-2">
        {item.title}
      </h3>
      {item.desc && (
        <p className="text-sm text-[#E0D5B8] leading-relaxed">{item.desc}</p>
      )}
    </div>
  );
}

function ChapterBanner({ title }) {
  return (
    <div className="relative flex items-center justify-center px-2">
      {/* Backdrop pill so the center timeline line disappears behind it */}
      <div className="relative mx-auto bg-[#0B2820] px-4 sm:px-8 py-2.5 sm:py-3 border border-[#D4AF37]/50 rounded-sm shadow-[0_14px_38px_-20px_rgba(0,0,0,0.9)] max-w-full">
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="hidden sm:inline-block h-px w-6 sm:w-10 bg-gradient-to-r from-transparent to-[#D4AF37]" />
          <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37] shrink-0" />
          <h3 className="font-display text-[10px] sm:text-sm tracking-[0.25em] sm:tracking-[0.4em] text-[#F3E08A] uppercase text-center">
            {title}
          </h3>
          <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37] shrink-0" />
          <span className="hidden sm:inline-block h-px w-6 sm:w-10 bg-gradient-to-l from-transparent to-[#D4AF37]" />
        </div>
      </div>
    </div>
  );
}
